import axios from "axios";
import Vue from "vue";
import store from "@/store/index";
import router from "@/router";
import AuthService from "@/auth/authService";
import API_CONFIG from "@/config/API_CONFIG";
import IApplication from "@/interfaces/ApplicationInterface";
import EVENTS from "@/constants/events";
import MODULE from "@/constants/modules";
import APP from "@/constants/application";
import NAVIGATION_DATA from "@/config/NAVIGATION_DATA";
import DEFAULT_APP_CONFIG from "@/config/DEFAULT_APP_CONFIG";
import { baseSaturnApiUrl } from './_endpoint';

let config = new API_CONFIG();
let apiUrl = baseSaturnApiUrl;
let apiKey = config.API_KEY;
let apiHeaderObj;

let modalStatus = {
  hideShowModal: false,
  modalName: "",
  modalTitle: "",
  modalSaveButtonTitle: "",
  isSaveButtonHidden: false,
  hideBottomBar: false,
  UI_TYPE: 0, //APP.UI_TYPE.Modal //The type of modal or new tab that should be opened.
  moduleId: null,
  data: {},
  dataObj: {},
  isInEditMode: false, //Whether the modal box is currently editing an item or crating a new item
  actionId: 0,
};

let sidePanel = {
  isSidePanelFrame: false,
  isOpen: true,
  name: "MainSidePanel", //Name of the side panel, incase multiple panels are open
  data: null, //The data to be passed into the side panel as prop value
  panelObj: null, //Reference to the side panel object, so we can close it anywhere.
};

class ApplicationService {
  constructor() {
    apiHeaderObj = {
      headers: {
        "x-functions-key": apiKey,
      },
    };
  }

  actionNavigation(action) {
    //If action is a modal or link
    if (action.UI_TYPE === APP.UI_TYPE.Modal) {
      this.showModal(action);
    } else if (action.UI_TYPE === APP.UI_TYPE.ModalIFrame) {
      //Used to support iframe image preview passing (backward compability)
      if (action.data.url) {
        //Pass data to global data store
        store.dispatch("setApplicationModalIframe", {
          url: action.data.url,
          title: "",
          showToolbar: true,
          data: document,
        });
      }

      this.showModal(action);
    }
    //Open Link to a new tab
    else if (action.UI_TYPE == APP.UI_TYPE.NewTab) {
      this.openNewTab(action.url);
    }
  }

  //Open/Show details pane
  showSidePanel(listDataObj, data, route) {
    let isIframe = false;

    if (listDataObj) {
      if (listDataObj.isSidePanelFrame) isIframe = true;
    }

    this.setPanelProperties(data, route, isIframe);

    //this.trackAnalytics(listDataObj.tracking);
  }

  setPanelProperties(data, route, isIframe) {
    sidePanel = store.getters.getSidePanel;

    sidePanel.isOpen = true;

    if (isIframe) sidePanel.isSidePanelFrame = true;
    else sidePanel.isSidePanelFrame = false;

    sidePanel.name = "MainSidePanel"; //Name of the side panel, incase multiple panels are open

    sidePanel.data = data;

    // Only call panelObj.show() if panelObj exists (for non-router based panels)
    if (sidePanel.panelObj && sidePanel.panelObj.show) {
      sidePanel.panelObj.show();
      sidePanel.panelObj.promise.then((result) => { });
    }

    //Send Data to Side Panel
    store.dispatch("setSidePanel", sidePanel);
  }

  closeSidePanel() {
    // Get fresh reference to sidePanel from store
    const currentSidePanel = store.getters.getSidePanel;

    // Check if panelObj exists before trying to hide it
    if (currentSidePanel.panelObj && currentSidePanel.panelObj.hide) {
      currentSidePanel.panelObj.hide();
    }

    //this.$emit("closePanel", {});

    //Clear Side Panel Data - only update the properties we want to change
    store.dispatch("setSidePanel", {
      isOpen: false,
      data: null
      // panelObj is preserved by merge logic
    });
  }

  //Open Modal Dialog Box and pass data paramaters.
  showModal(action) {
    modalStatus.hideShowModal = true;
    modalStatus.modalName = action.componentName;
    modalStatus.data = action.data;

    //Automatically assign values from action to modal status
    // Object.assign(modalStatus, action);
    modalStatus = {...modalStatus, ...action};

    // Map hideDefaultButtons to hideBottomBar for modal layout compatibility
    if (action.hideDefaultButtons !== undefined) {
      modalStatus.hideBottomBar = action.hideDefaultButtons;
    }

    store.dispatch("toggleModalStatus", modalStatus);
  }

  //Get Application Navigation
/**
 * Extracts tenant ID and user ID from a query string.
 * @param {string} queryString - The query string to extract parameters from.
 * @returns {Object} An object containing tenant ID and user ID.
 */
 extractTenantAndUserFromQuery(queryString) {
    let params = new URLSearchParams(queryString);
    return {
        tenantId: params.get("t"),
        userId: params.get("u"),
    };
}

/**
 * Fetches the print data from the API and attaches tenantId and userId.
 * @param {number} moduleId - The module ID.
 * @param {number} id - The ID.
 * @returns {Promise<Object>} The API response with tenantId and userId attached.
 */
async print(moduleId, id) {
    let auth = AuthService.getInstance();
    let config = new API_CONFIG();
    let queryString = auth.getQueryString();
    let extractedParams = this.extractTenantAndUserFromQuery(queryString);
    
    let dataResults;
  axios.defaults.baseURL = apiUrl;

    let param = "&id=" + id + "&moduleid=" + moduleId;

    try {
        dataResults = await axios.get("/print" + queryString + param, apiHeaderObj);

        // Attach tenantId and userId to the returned object
        return {
            ...dataResults.data.resources,
            t: extractedParams.tenantId,
            u: extractedParams.userId
        };
    } catch (error) {
        //console.error("Error fetching print data:", error);
        throw error;
    }
}


  //Get all application settings
  async getApplicationConfiguration(): Promise<IApplication> {
    let auth = AuthService.getInstance();
    let config = new API_CONFIG();
    let queryString = auth.getQueryString();
    let dataResults;

    axios.defaults.baseURL = apiUrl;

    try {
      dataResults = await axios.get("/application/settings" + queryString, apiHeaderObj);
      
      // Check if the response status is 200 OK
      if (dataResults.status === 200) {
        return dataResults.data.resources;
      } else {
        //console.warn(`Received non-200 status code: ${dataResults.status}, using fallback settings`);
        return DEFAULT_APP_CONFIG;
      }
    } catch (error) {
      // Handle network errors, timeouts, or any other exceptions
      //console.warn("Failed to fetch application configuration, using fallback settings", error);
      return DEFAULT_APP_CONFIG;
    }
  }

  //Get Application Navigation
  async getNavigation() {
    // Clone the navigation data to avoid mutating the original
    const navigationData = JSON.parse(JSON.stringify(NAVIGATION_DATA.resources));

    // Update calendar navigationURL for localhost mode
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      // Find the calendar navigation item (id: 5)
      const calendarItem = navigationData.leftNav.find((item: any) => item.id === 5);
      if (calendarItem) {
        calendarItem.navigationURL = 'http://localhost:3000/';
      }
    }

    return navigationData;
  }

  async getLatestVersion() {
    // Return hardcoded version data instead of making an API call
    return {
      "application": "Jupiter",
      "deployment": "Production",
      "id": "9e9fb244-01e5-4690-97b5-3f46a0032af5",
      "release": "20210707.1",
      "releaseId": "82e0dc99-aae1-4014-8761-d68c4213344c",
      "_rid": "RB1fANz2gQ0hsgIAAAAAAA==",
      "_self": "dbs/RB1fAA==/colls/RB1fANz2gQ0=/docs/RB1fANz2gQ0hsgIAAAAAAA==/",
      "_etag": "\"04000615-0000-0300-0000-60e5f23d0000\"",
      "_attachments": "attachments/",
      "_ts": 1625682493
    };
  }

  async openNewTab(url) {
    if (url) window.open(url, "_blank");
  }

  //Send's Data to google tag Manager and to other external api tracking source
  trackAnalytics(tracking) {
    //ONLY TRACK EVENTS IN PRODUCTION
    // if (process.env.VUE_APP_ENVIRONMENT === "production") {
    //   Vue.gtm.trackEvent({
    //     event: null, // Event type [default = 'interaction'] (Optional)
    //     category: tracking.ModuleName, //"PROJECTS",
    //     action: tracking.ModuleAction, //EVENTS.PROJECTS.View,
    //     label: tracking.Description, //"Viewed Project",
    //     value: tracking.Id, // data.intProjectID
    //     noninteraction: false, // Optional
    //   });
    // }
  }
}

export default ApplicationService;
